import React, { useState, useRef, useEffect } from 'react'
import "../../css/CameraView.css"
import Webcam from "react-webcam"
import Spinner from '../common/Spinner'
import PermissionModal from '../modals/PermissionModal'
import { CSSTransition } from 'react-transition-group'
import FaceGuide from "../../image/face-guide.png"
import shutterSound from "../../sound/camera-shutter.mp3"
import beepSound from "../../sound/beep.mp3"
import { cropImage } from "../../utils/ImageUtil"
import { useCameraPermission } from "../../hooks/useCameraPermission"

interface CameraViewProps {
  theme: any
  startCapture: boolean
  selectedCamera: MediaDeviceInfo | null
  showFaceGuide?: boolean
  postDelay: number
  handleDevices: (devices: MediaDeviceInfo[]) => void
  finishCountdown: () => void
  handleImage: (image: string, source: string) => void
  handleError: (error: string) => void
}

function CameraView({ theme, startCapture, selectedCamera, showFaceGuide, postDelay, handleDevices, finishCountdown, handleImage, handleError }: CameraViewProps) {
  const [timerValue, setTimerValue] = useState(-1)
  const [showModal, setShowModal] = useState(false)
  const [mediaReceived, setMediaReceived] = useState(false)
  const [sequenceStarted, setSequenceStarted] = useState(false)
  const [freezeFrame, setFreezeFrame] = useState(null)
  const webcam = useRef<any>(null)
  const canvas = useRef<HTMLCanvasElement | null>(null)

  const { isGranted, isLoading } = useCameraPermission()

  const receiveFirstMedia = () => {
    setMediaReceived(true)
    setShowModal(false)

    navigator.mediaDevices.enumerateDevices().then(mediaDevices => {
      let videoDevices = mediaDevices.filter(({ kind }) => kind === "videoinput")
      handleDevices(videoDevices)
    })
  }

  // Show permission modal after delay, but only if permission isn't already granted
  useEffect(() => {
    if (isLoading || isGranted || mediaReceived) return

    const timeoutId = setTimeout(() => {
      if (!mediaReceived) {
        setShowModal(true)
      }
    }, 800)

    return () => clearTimeout(timeoutId)
  }, [isLoading, isGranted, mediaReceived])

  const startSequence = () => {
    setTimerValue(3)
    tick(3)
  }

  const tick = (currentValue: number) => {
    if (currentValue >= 1) {
      setTimeout(() => {
        let newValue = currentValue - 1
        setTimerValue(newValue)
        if (newValue > 0) {
          tick(newValue)
        }
        if (newValue === 0) {
          finishCountdown()
          setTimeout(() => {
            captureImage()
          }, 200)
        }
      }, 1000)
    }
  }

  const captureImage = () => {
    if (!webcam || !webcam.current) {
      return
    }

    let imageData = webcam.current.getScreenshot()
    let image = new Image()
    image.src = imageData
    image.onload = function () {
      let cropPixels = {
        x: 0,
        y: (image.naturalHeight - image.naturalWidth) / 2,
        width: image.naturalWidth,
        height: image.naturalWidth
      }

      if (image.naturalWidth > image.naturalHeight) {
        cropPixels = {
          x: (image.naturalWidth - image.naturalHeight) / 2,
          y: 0,
          width: image.naturalHeight,
          height: image.naturalHeight
        }
      }

      if (!canvas.current) {
        throw new Error("Canvas missing")
      }

      imageData = cropImage(image, canvas.current, cropPixels, mirrored)

      setFreezeFrame(imageData)
      setTimeout(() => {
        handleImage(imageData, "camera")
      }, postDelay)
    }
  }

  // Start countdown
  if (startCapture && timerValue === -1 && !sequenceStarted) {
    setSequenceStarted(true)
    setTimeout(startSequence, 800)
  }

  let videoConstraints: any = {
    aspectRatio: 1,
    width: 1920,
    height: 1080
  }

  let mirrored = true
  if (selectedCamera) {
    videoConstraints.deviceId = selectedCamera.deviceId
    if (selectedCamera.label.toLowerCase().includes("back")) {
      mirrored = false
    }
  }

  // Play beep sound on each countdown tick
  useEffect(() => {
    if (timerValue > 0) {
      const audio = new Audio(beepSound)
      audio.volume = 0.5
      audio.play()
    }
  }, [timerValue])

  // Play shutter sound when capture completes
  useEffect(() => {
    if (timerValue === 0) {
      const audio = new Audio(shutterSound)
      audio.play()
    }
  }, [timerValue])

  return (
    <>
      <canvas className="hiddenCanvas" ref={canvas}></canvas>

      <CSSTransition in={timerValue === 0} appear timeout={{ enter: 300, exit: 0 }} classNames="flash-animation" unmountOnExit>
        <div className="flash"></div>
      </CSSTransition>

      <CSSTransition in appear timeout={500} exit={false} classNames="camera" unmountOnExit>
        <div className="boothContainer">
          <div className="square">
            <div className="countdown">{timerValue > 0 && timerValue}</div>

            <CSSTransition in={theme.firstOverlayUrl && mediaReceived} appear timeout={500} classNames="fade" unmountOnExit>
              <div className="overlay">
                <img className="fill" crossOrigin="anonymous" src={theme.firstOverlayUrl} alt="Overlay" />
              </div>
            </CSSTransition>

            <CSSTransition in={showFaceGuide} appear timeout={500} classNames="fade" unmountOnExit>
              <div className="overlay">
                <img className="fill" crossOrigin="anonymous" src={FaceGuide} alt="Guide" />
              </div>
            </CSSTransition>

            {freezeFrame &&
              <div className="foregroundImage">
                <img src={freezeFrame ?? ""} className="fill" alt="Freeze frame" />
              </div>
            }

            <div className="videoContainer">
              <Webcam
                ref={webcam}
                audio={false}
                mirrored={mirrored}
                imageSmoothing
                forceScreenshotSourceSize
                videoConstraints={videoConstraints}
                onUserMedia={receiveFirstMedia}
                onUserMediaError={(exception) => {
                  handleError(typeof exception === "string" ? exception : exception.message)
                }}
              />
            </div>

            {timerValue === -1 &&
              <div className="underCamera">
                <Spinner color={theme.textStyle.color} centerVertical />
              </div>
            }
          </div>
        </div>
      </CSSTransition>

      <PermissionModal show={showModal} />
    </>
  )
}

export default CameraView
